/* eslint-disable import/no-cycle */
import Manufacturer from '@/models/vehicle/manufacturer';
import PersonalDetails from '@/models/PersonalDetails';
import Range from '@/models/vehicle/range';
import ExtraData from '@/models/ExtraData';
import Model from '@/models/vehicle/model';
import Derivative from '@/models/vehicle/derivative';
import { store } from '../store';

class TokenService {
  private static serviceInstance: TokenService;

  public static get instance() {
    // eslint-disable-next-line no-return-assign
    return this.serviceInstance || (this.serviceInstance = new this());
  }

  public get manufacturer(): Manufacturer {
    return store.getters['vehicleModule/currentManufacturer'];
  }

  public get range(): Range {
    return store.getters['vehicleModule/currentRange'];
  }

  public get model(): Model {
    return store.getters['vehicleModule/currentModel'];
  }

  public get derivative(): Derivative {
    return store.getters['vehicleModule/currentDerivative'];
  }

  public replaceToken(text: string): string {
    text = this.replaceTitle(text);
    text = this.replaceSubTitle(text);
    text = this.replaceManufacturerName(text);
    text = this.replaceRangeName(text);
    text = this.replaceModelName(text);
    text = this.replaceVehicleName(text);
    if (text.includes('vehicleType')) {
      text = this.replaceVehicleType(text);
    }
    text = this.replaceFirstName(text);
    text = this.replaceXSaleVehicle(text);
    text = this.replaceMonthName(text);
    text = this.replaceAveragePercent(text);
    text = this.replaceTlaYears(text);
    text = this.replaceUKPlate(text);
    text = this.replaceRetailer(text);
    text = this.replaceDerivativeId(text);
    text = this.replaceVehicleNameWithModel(text);
    return text;
  }

  private replaceManufacturerName(text: string): string {
    if (this.manufacturer === null || typeof this.manufacturer === 'undefined') {
      return text.replace('manufacturerName', 'vehicleType');
    }

    return text.replace('manufacturerName', this.manufacturer.manufacturerName);
  }

  private replaceRangeName(text: string): string {
    if (this.range === null || typeof this.range === 'undefined') {
      return text.replace('rangeName', '');
    }

    return text.replace('rangeName', this.range.rangeName);
  }

  private replaceModelName(text: string): string {
    if (this.model === null || typeof this.model === 'undefined') {
      return text.replace('modelName', '');
    }

    return text.replace('modelName', this.model.modelName);
  }

  private replaceTitle(text: string): string {
    return text.replace('pageTitle', this.pageTitle);
  }

  private get pageTitle(): string {
    let pageTitle: string | null = null;
    try {
      const urlParams = new URLSearchParams(window.location.search);

      pageTitle = urlParams.get('pageTitle');
    } catch {
      return '';
    }

    if (pageTitle && pageTitle.length > 0) {
      return pageTitle;
    }

    return '';
  }

  private replaceSubTitle(text: string): string {
    return text.replace('pageSubTitle', this.pageSubTitle);
  }

  private get pageSubTitle(): string {
    let pageSubTitle: string | null = null;
    try {
      const urlParams = new URLSearchParams(window.location.search);

      pageSubTitle = urlParams.get('pageSubTitle');
    } catch {
      return '';
    }

    if (pageSubTitle && pageSubTitle.length > 0) {
      return pageSubTitle;
    }

    return '';
  }

  private get vehicleName(): string {
    let tlaVehicleName: string | null = null;
    try {
      const urlParams = new URLSearchParams(window.location.search);

      tlaVehicleName = urlParams.get('tlaVehicleName');
    } catch {
      tlaVehicleName = '';
    }

    if (tlaVehicleName && tlaVehicleName.length > 0) {
      return tlaVehicleName;
    }

    if (this.manufacturer === null || typeof this.manufacturer === 'undefined') {
      return '';
    }

    tlaVehicleName = `${this.manufacturer.manufacturerName} ${this.range !== null ? this.range.rangeName : ''} ${this.model !== null ? this.model.modelName : ''}`;

    return tlaVehicleName;
  }

  private replaceVehicleNameWithModel(text: string): string {
    const tlaVehicleName = this.vehicleName;

    return text.replace('vehicleWithModel', tlaVehicleName || '');
  }

  private replaceVehicleName(text: string): string {
    const tlaVehicleName = this.vehicleName;

    if (tlaVehicleName.length > 0) {
      return text.replace('vehicleName', tlaVehicleName);
    }

    return text.replace('vehicleName', 'vehicleType');
  }

  private replaceVehicleType(text: string): string {
    const vehicleType: string = store.getters['initialSettingsModule/vehicleTypeName'];
    return text.replace(/vehicleType/g, vehicleType);
  }

  private replaceFirstName(text: string): string {
    const personalDetails: PersonalDetails = store.getters['personalDetailsModule/personalDetails'];
    if (personalDetails.firstName === null) {
      return text.replace('firstName', '');
    }

    return text.replace('firstName', personalDetails.firstName);
  }

  private replaceXSaleVehicle(text: string): string {
    return text.replace('the xSaleVehicle', 'our recommended vehicle');
  }

  private replaceMonthName(text: string): string {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];

    return text.replace('monthName', monthNames[new Date().getMonth()]);
  }

  private replaceTlaYears(text: string): string {
    const foundingDate = new Date('2002/02/02');
    const today = new Date();

    let age = today.getFullYear() - foundingDate.getFullYear();
    const m = today.getMonth() - foundingDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < foundingDate.getDate())) {
      age -= 1;
    }

    return text.replace('tlaYears', age.toString());
  }

  private replaceUKPlate(text: string): string {
    const today = new Date();
    const twoDigitsCurrentYear = parseInt(today.toLocaleDateString('en', { year: '2-digit' }), 10);
    const currentMonth = today.getMonth();
    let currentPlate = 0;

    if (currentMonth >= 2 && currentMonth < 8) {
      currentPlate = twoDigitsCurrentYear;
    }

    if (currentMonth < 2) {
      currentPlate = twoDigitsCurrentYear + 49;
    }

    if (currentMonth >= 8) {
      currentPlate = twoDigitsCurrentYear + 50;
    }

    return text.replace('ukPlate', currentPlate.toString());
  }

  private replaceAveragePercent(text: string): string {
    if (text.includes('averagePercent')) {
      const averagePercent = store.getters['vehicleModule/averagePercentSaving'];
      return text.replace(/averagePercent/g, `${averagePercent}%`);
    }

    return text;
  }

  private replaceDerivativeId(text: string): string {
    if (text.includes('vehicleDerivativeId') && this.derivative) {
      return text.replace(/vehicleDerivativeId/g, this.derivative.derivativeId.toString());
    }

    return text;
  }

  private replaceRetailer(text: string): string {
    const extraData: ExtraData[] = store.getters['enquiryModule/extraData'];

    if (!extraData) {
      return text;
    }

    if (text.includes('retailerName')) {
      const retailerName = extraData.find((x: ExtraData) => x.keyName === 'RetailerName');
      text = text.replace(/retailerName/g, retailerName && retailerName.keyValue ? retailerName.keyValue : '');
    }

    if (text.includes('retailerAddressLn1')) {
      const retailerAddressLn1 = extraData.find((x: ExtraData) => x.keyName === 'RetailerAddressLine1');
      text = text.replace(/retailerAddressLn1/g, retailerAddressLn1 && retailerAddressLn1.keyValue ? retailerAddressLn1.keyValue : '');
    }

    if (text.includes('retailerTown')) {
      const retailerTown = extraData.find((x: ExtraData) => x.keyName === 'RetailerAddressTown');
      text = text.replace(/retailerTown/g, retailerTown && retailerTown.keyValue ? retailerTown.keyValue : '');
    }

    if (text.includes('retailerCounty')) {
      const retailerCounty = extraData.find((x: ExtraData) => x.keyName === 'RetailerAddressCounty');
      text = text.replace(/retailerCounty/g, retailerCounty && retailerCounty.keyValue ? retailerCounty.keyValue : '');
    }

    if (text.includes('retailerPostcode')) {
      const retailerPostcode = extraData.find((x: ExtraData) => x.keyName === 'RetailerAddressPostcode');
      text = text.replace(/retailerPostcode/g, retailerPostcode && retailerPostcode.keyValue ? retailerPostcode.keyValue : '');
    }

    return text;
  }
}

// export a singleton instance in the global namespace
// eslint-disable-next-line import/prefer-default-export
export const tokenService = TokenService.instance;
